import React, { useState } from 'react';
import { FilterBar } from '../components/FilterBar';
import { useSchoolSearch } from '../hooks/useSchoolSearch';
import { useMetaTags } from '../hooks/useMetaTags';
import { areaOfStudyData } from '../data/areaOfStudyData';
import { getDefaultProgram } from '../data/filterOptions';
import { SchoolListing } from '../types/api';
import { ExternalLinkIcon, ShieldCheckIcon, StarIcon, BuildingIcon, MapPinIcon } from 'lucide-react';
export function CollegesPage() {
  const [degreeLevel, setDegreeLevel] = useState<number>(3);
  const [selectedAreaId, setSelectedAreaId] = useState<number>(1);
  const [selectedProgram, setSelectedProgram] = useState<number>(100);
  // Meta tags
  useMetaTags({
    title: 'Explore Accredited Online Colleges & Universities | DegreeCompare',
    description: 'Browse top-rated accredited online colleges and universities. Compare programs, read reviews, and find the right school for your online degree.',
    keywords: 'online colleges, accredited universities, online degree programs, best online schools, college directory, online education, distance learning',
    ogTitle: 'Explore Accredited Online Colleges & Universities',
    ogDescription: 'Browse and compare top-rated accredited online colleges. Find the right school for your online degree with detailed program information and reviews.'
  });
  const {
    schools,
    loading,
    error
  } = useSchoolSearch({
    degreeInterest: degreeLevel,
    mProgram: selectedProgram
  });
  const handleAreaChange = (areaId: number) => {
    setSelectedAreaId(areaId);
    setSelectedProgram(getDefaultProgram(areaId));
  };
  return <div data-id="element-593">
      {/* Hero */}
      <section className="w-full bg-[#1a2332] text-white py-10 md:py-14 px-4 relative overflow-hidden" data-id="element-594">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#2a374a] to-transparent opacity-50 pointer-events-none" data-id="element-595"></div>
        <div className="max-w-7xl mx-auto relative z-10" data-id="element-596">
          <div className="flex items-center gap-3 mb-4" data-id="element-597">
            <div className="bg-white/10 p-2 rounded-lg" data-id="element-598">
              <BuildingIcon className="w-6 h-6" data-id="element-599" />
            </div>
            <span className="text-sm font-medium text-gray-300 uppercase tracking-wider" data-id="element-600">
              College Directory
            </span>
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight mb-3" data-id="element-601">
            Explore Accredited Online Colleges
          </h1>
          <p className="text-base text-gray-300 max-w-xl" data-id="element-602">
            Browse top-rated online colleges and universities. Click any school
            to learn more about their programs.
          </p>
        </div>
      </section>

      {/* Filter Bar */}
      <div className="w-full bg-white border-b border-gray-200 py-4 px-4 shadow-sm relative z-30" data-id="element-603">
        <div className="max-w-7xl mx-auto" data-id="element-604">
          <FilterBar degreeLevel={degreeLevel} areaStudy={selectedAreaId} program={selectedProgram} onDegreeLevelChange={setDegreeLevel} onAreaStudyChange={handleAreaChange} onProgramChange={id => setSelectedProgram(id)} data-id="element-605" />
        </div>
      </div>

      {/* College Grid */}
      <div className="max-w-7xl mx-auto px-4 py-8 md:py-10" data-id="element-606">
        {error && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-6" data-id="element-607">
            <p className="font-medium" data-id="element-608">Error loading colleges</p>
            <p className="text-sm" data-id="element-609">{error}</p>
          </div>}

        {loading ? <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-id="element-610">
            {[1, 2, 3, 4, 5, 6].map(i => <div key={i} className="bg-white rounded-xl border border-gray-200 p-6 animate-pulse" data-id="element-611">
                <div className="w-16 h-16 bg-gray-200 rounded-lg mb-4" data-id="element-612"></div>
                <div className="h-5 bg-gray-200 rounded w-3/4 mb-3" data-id="element-613"></div>
                <div className="h-4 bg-gray-200 rounded w-full mb-2" data-id="element-614"></div>
                <div className="h-4 bg-gray-200 rounded w-2/3" data-id="element-615"></div>
              </div>)}
          </div> : schools.length === 0 ? <div className="bg-gray-50 border border-gray-200 rounded-xl p-12 text-center" data-id="element-616">
            <BuildingIcon className="w-10 h-10 text-gray-300 mx-auto mb-3" data-id="element-617" />
            <p className="text-gray-600 font-medium" data-id="element-618">
              No colleges found for this selection.
            </p>
            <p className="text-sm text-gray-500 mt-1" data-id="element-619">
              Try adjusting your degree level or program filters.
            </p>
          </div> : <>
            <p className="text-sm text-gray-500 mb-6" data-id="element-620">
              Showing {schools.length} college{schools.length !== 1 ? 's' : ''}{' '}
              for your selection
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" data-id="element-621">
              {schools.map((school, index) => <CollegeCard key={`${school.mBrandID}-${index}`} school={school} data-id="element-622" />)}
            </div>
          </>}
      </div>
    </div>;
}
function CollegeCard({
  school
}: {
  school: SchoolListing;
}) {
  return <a href={school.clickURL} target="_blank" rel="noopener noreferrer" className="group bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg hover:border-gray-300 transition-all flex flex-col" data-id="element-623">
      <div className="p-5 flex flex-col flex-grow" data-id="element-624">
        {/* Logo + Name */}
        <div className="flex items-start gap-4 mb-4" data-id="element-625">
          <div className="w-14 h-14 flex-shrink-0 flex items-center justify-center bg-gray-50 rounded-lg border border-gray-100 p-1.5" data-id="element-626">
            <img src={school.imageURL} alt={`${school.mBrandName} logo`} className="max-w-full max-h-full object-contain" data-id="element-627" />
          </div>
          <div className="min-w-0" data-id="element-628">
            <h3 className="text-base font-bold text-gray-900 group-hover:text-[#3b82f6] transition-colors leading-snug" data-id="element-629">
              {school.mBrandName}
            </h3>
            {school.schoolHighlight && <span className="inline-flex items-center gap-1 mt-1 text-[11px] font-semibold text-amber-700 bg-amber-50 px-2 py-0.5 rounded-full" data-id="element-630">
                <StarIcon className="w-3 h-3 fill-current" data-id="element-631" />
                {school.schoolHighlight}
              </span>}
          </div>
        </div>

        {/* Description */}
        {school.aboutCollege ? <p className="text-sm text-gray-600 leading-relaxed mb-4 flex-grow line-clamp-3" data-id="element-632">
            {school.aboutCollege}
          </p> : school.headContent ? <p className="text-sm text-gray-600 leading-relaxed mb-4 flex-grow line-clamp-3" data-id="element-633">
            {school.headContent}
          </p> : <p className="text-sm text-gray-600 leading-relaxed mb-4 flex-grow" data-id="element-634">
            Accredited online programs with flexible scheduling options.
          </p>}

        {/* Accreditation */}
        {school.accreditation && <div className="flex items-start gap-2 mb-4 text-xs text-gray-500" data-id="element-635">
            <ShieldCheckIcon className="w-3.5 h-3.5 text-green-500 flex-shrink-0 mt-0.5" data-id="element-636" />
            <span className="line-clamp-1" data-id="element-637">{school.accreditation}</span>
          </div>}

        {/* CTA */}
        <div className="pt-3 border-t border-gray-100 mt-auto flex items-center justify-between" data-id="element-638">
          <span className="text-xs text-gray-500" data-id="element-639">100% Online</span>
          <span className="text-sm font-bold text-[#e8594f] flex items-center gap-1.5 group-hover:gap-2 transition-all" data-id="element-640">
            Visit School <ExternalLinkIcon className="w-3.5 h-3.5" data-id="element-641" />
          </span>
        </div>
      </div>
    </a>;
}